import {
  achievementsFromResponse,
  recordTabVisit,
  type Achievement,
  type AchievementAction,
} from "@core/economy/achievements";
import { checkAchievements, claimAchievementsForAction } from "../data-access/achievementsSupabase";
import { getSessionSafe } from "../lib/authSession";
import { TABS } from "../lib/tabNavigation";
import { showAchievementToast } from "../lib/toast";

/**
 * ⚠ 2026-09-15: 모바일에서 업적 claim을 부르는 곳이 글·댓글 2곳뿐이었다.
 *   서버는 claim_achievements_for_action을 **클라가 불러야만** 판정한다(트리거·cron 대행 없음 — 라이브 확인).
 *   그래서 모바일로만 가입·활동한 사용자는 첫걸음·첫 추천·탭 탐험가·출석 업적을 딸 수 없었다.
 *   (웹은 onboarding·vote·tab_explorer·attendance를 각 화면에서 부른다.)
 *   출석은 서버 라우트(/api/mobile/scrap/attend)가 claim까지 하고, 여기선 그 결과를 토스트만 한다.
 */

// 같은 action의 동시 호출을 하나로 묶는다.
//   unlock_achievement는 "이미 있나 조회 → INSERT → 보상" 순서라 두 요청이 동시에 들어오면
//   둘 다 '없음'을 보고 EXP·BULL을 이중 지급할 수 있다(StrictMode 이중 effect·연타).
const inflight = new Map<string, Promise<Achievement[]>>();

// 토스트는 항목 하나만 받는다 — forEach(showAchievementToast)는 (값, 인덱스, 배열)을 넘기고
//   RPC의 icon은 null일 수 있어 토스트 타입(string|undefined)과 어긋난다.
function toastOne(ach: Achievement): void {
  showAchievementToast({ name: ach.name, icon: ach.icon ?? undefined });
}

/**
 * 업적 claim + 신규 해금 토스트. 실패는 삼킨다 — 추천·가입 같은 본 행동을 막지 않는다.
 * 동시 호출 중인 같은 action은 첫 요청의 결과를 공유하고 토스트는 한 번만 띄운다.
 */
export function claimAndToast(action: AchievementAction): Promise<Achievement[]> {
  const pending = inflight.get(action);
  if (pending) return pending.then(() => []);

  const run = (async () => {
    try {
      const achs = await checkAchievements(action);
      achs.forEach(toastOne);
      return achs;
    } catch (e: any) {
      console.warn(`[achievements] ${action} claim 예외:`, e?.message);
      return [];
    } finally {
      inflight.delete(action);
    }
  })();
  inflight.set(action, run);
  return run;
}

/** 서버 응답에 실려 온 신규 해금 목록을 토스트로 띄운다 (필드 없음·깨진 값은 조용히 무시) */
export function toastAchievementsFrom(value: unknown): Achievement[] {
  const achs = achievementsFromResponse(value);
  achs.forEach(toastOne);
  return achs;
}

// ── 탭 탐험가 ────────────────────────────────────────────────
// 모바일 하단 탭 5개(홈·모의투자·복기·커뮤니티·MY)를 전부 방문하면 tab_explorer claim.
// 🔑 저장 키에 user id를 넣는다 — 같은 기기에서 계정을 바꾸면 다른 계정의 방문이 섞인다(N-40과 같은 함정).
// 🔑 done은 claim이 **성공(ok)** 했을 때만 찍는다 — 오프라인에서 실패하면 다음 탭 전환 때 재시도.
const TAB_EXPLORER_VISITED = "sotda-tab-explorer-visited";
const TAB_EXPLORER_DONE = "sotda-tab-explorer-done";
const TAB_ROOTS: readonly string[] = TABS.map((t) => t.to);

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 저장소를 못 쓰면 다음 방문 때 다시 센다 — 서버 claim이 멱등이라 안전 */
  }
}

let tabClaimInflight = false;

/**
 * 탭 방문 기록 → 5탭 완주 시 tab_explorer claim + 토스트.
 * @param tabRoot resolveTabRoot(pathname) 결과 (탭 밖 화면이면 null)
 */
export async function trackTabExplorer(tabRoot: string | null | undefined): Promise<void> {
  if (!tabRoot) return;
  const { session } = await getSessionSafe();
  const uid = session?.user?.id;
  if (!uid) return; // 미로그인 — 게스트는 업적이 없다(RPC도 authenticated 전용)

  if (readStore(`${TAB_EXPLORER_DONE}:${uid}`)) return;

  const visitedKey = `${TAB_EXPLORER_VISITED}:${uid}`;
  let saved: unknown = [];
  try {
    saved = JSON.parse(readStore(visitedKey) || "[]");
  } catch {
    saved = [];
  }
  const next = recordTabVisit(saved, tabRoot, TAB_ROOTS);
  if (next.changed) writeStore(visitedKey, JSON.stringify(next.visited));
  if (!next.complete || tabClaimInflight) return;

  tabClaimInflight = true;
  try {
    const res = await claimAchievementsForAction("tab_explorer");
    if (!res.ok) return; // 실패 — done을 안 찍어 다음 탭 전환 때 다시 시도
    writeStore(`${TAB_EXPLORER_DONE}:${uid}`, "1");
    res.unlocked.forEach(toastOne);
  } finally {
    tabClaimInflight = false;
  }
}
